import React from 'react';
import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  createStyles,
  Divider,
  Grid,
  makeStyles,
  Theme,
} from '@material-ui/core';
import { Field } from 'react-final-form';
import {
  AutoFocusedForm,
  notEmpty,
  PhotoField,
  TextField,
  CheckboxField,
  required,
  moreThanZero,
} from 'components';
import { MutableButton } from 'ui/MutableButton';
import { RedButton } from 'components/Buttons';
import { usePrizeForm } from './usePrizeForm';
import { Prize, PrizeFormValues } from './types';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      minWidth: 600,
    },
    actions: {
      padding: theme.spacing(2),
      justifyContent: 'flex-end',
    },
    accessButton: {
      color: theme.palette.common.white,
      backgroundColor: theme.palette.success.dark,
      '&:hover': {
        color: theme.palette.success.dark,
        backgroundColor: theme.palette.success.light,
      },
    },
  })
);

type Props = {
  seasonId: string;
  prize?: Prize | null;
  withCode: boolean;
  onClose: () => void;
};

export const PrizeForm = ({ seasonId, prize, withCode, onClose }: Props) => {
  const classes = useStyles();
  const { initialValues, onSubmit, loading, isEdit } = usePrizeForm({
    seasonId,
    prize,
    withCode,
    onClose,
  });

  const title = isEdit
    ? withCode
      ? 'Редактирование приза с кодом'
      : 'Редактирование приза'
    : withCode
    ? 'Создание приза с кодом'
    : 'Создание приза';

  const submitButtonText = isEdit ? 'Сохранить' : 'Создать';

  return (
    <AutoFocusedForm<PrizeFormValues>
      onSubmit={onSubmit}
      initialValues={initialValues}
      render={({ handleSubmit, valid }) => {
        return (
          <form onSubmit={handleSubmit}>
            <Card className={classes.root}>
              <CardHeader title={title} />
              <Divider />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Field
                      name="imageId"
                      label="Изображение"
                      component={PhotoField}
                      url={prize?.image?.url}
                      validate={required}
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Field
                          name="title"
                          label="Название"
                          component={TextField}
                          type="text"
                          validate={notEmpty}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          name="price"
                          label="Стоимость"
                          component={TextField}
                          type="number"
                          validate={moreThanZero}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          name="description"
                          label="Описание"
                          component={TextField}
                          type="text"
                          multiline
                          rows={4}
                          validate={notEmpty}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <CheckboxField name="isPremium" label="Премиальный?" />
                      </Grid>
                      {withCode && (
                        <>
                          <Grid item xs={12}>
                            <Field
                              name="promoCode"
                              label="Промокод"
                              component={TextField}
                              type="text"
                              validate={notEmpty}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Field
                              name="siteUrl"
                              label="Ссылка на сайт"
                              component={TextField}
                              type="text"
                              validate={notEmpty}
                            />
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
              <CardActions className={classes.actions}>
                <MutableButton
                  className={classes.accessButton}
                  loading={loading}
                  color="primary"
                  disabled={!valid}
                  size="medium"
                  type="submit"
                  variant="contained"
                >
                  {submitButtonText}
                </MutableButton>
                <RedButton
                  color="primary"
                  size="medium"
                  type="button"
                  variant="contained"
                  onClick={onClose}
                >
                  Отменить
                </RedButton>
              </CardActions>
            </Card>
          </form>
        );
      }}
    />
  );
};
